'use client';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { Message } from 'ai/react';
import { useChat } from '@ai-sdk/react';
import {
  Dispatch,
  useEffect,
  useReducer,
  useState,
  useMemo,
  useCallback
} from 'react';

// Extend Window interface to include ellaDispatch
declare global {
  interface Window {
    ellaDispatch: Dispatch<EllaAction>;
  }
}

import './Ella.style.scss';

import Text from '@/components/common/Text/Text';
import EllaCart from '@/components/common/EllaCart/EllaCart';
import EllaCartConfirmation from '@/components/common/EllaCartConfirmation/EllaCartConfirmation';
import EllaChatInput from '@/components/common/EllaChatInput/EllaChatInput';
import EllaMessages from '@/components/common/EllaMessages/EllaMessages';

import { useUser } from '@/components/hook/user';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useScrollToBottom } from '@/components/hook/scrollToBottom';
import { useEllaAnalytics } from '@/components/hook/analytics';
import {
  fetchVivrelleCart,
  getTaxRateFromStripe
} from '@/util/cart_generic_client';

import { EPCCAddress, USER_STATUS } from '@/types/User';
import { ShippingMethod } from '@/types/ShippingMethods';
import { ProductCardData } from '@/types/Product';
import {
  CLOSET_SLOT_TIER_STRING,
  ClosetItem,
  ClosetSlot
} from '@/types/Closet';
import { Order, ORDER_ITEM_STATUS_INTERNAL } from '@/types/Order';
import {
  EllaPromptObject,
  initialPrompts,
  PROMPT_SHOW_OTHER_VIVRELLE_ITEMS_COPY
} from '@/types/Ella';
import { useCart } from '@/components/hook/cart';
import analytics from '@/util/analytics';
import ButtonScrollToBottom from '../ButtonScrollToBottom/ButtonScrollToBottom';
import { ELLA_RETAILER } from '../EllaCartProduct/EllaCartProduct';
import SideScrollElement from '../SideScrollElement/SideScrollElement';
import EllaPrompt from './EllaPrompt';
import { logEllaError } from '@/util/datadog';
import { useOnMount } from '@/components/hook/onMount';

type EllaProps = {
  /** Member's addresses, including billing and shipping */
  addresses: EPCCAddress[];
  className?: string;
  shippingMethods: ShippingMethod[];
  startNewStoreChat?: boolean;
};

type Bill = {
  subtotal: number;
  shipping: number;
  taxes: number;
  discountPercentage: number;
  taxRate: number;
};

export interface EllaState {
  bill: Bill;
  cart: ProductCardData[];
  revolveCartId?: string;
  shippingMethodId: string;
  shippingMethodCopy: string;
  showCart: boolean;
  showCartConfirmation: boolean;
  revolveOrder?: Order;
  vivrelleOrder?: Order;
  wishlistUpdated: boolean;
  limitReached: boolean;
  addresses: EPCCAddress[];
  errorSubmitOrder?: string;
  isProcessing: boolean;
}

export type EllaAction =
  | {
      type: 'ADD_TO_CART';
      payload: {
        cartItem: ProductCardData;
      };
    }
  | {
      type: 'REMOVE_FROM_CART';
      payload: {
        cartItemId: string;
      };
    }
  | {
      type: 'SET_SHIPPING_METHOD';
      payload: {
        shippingMethodId: string;
        shippingMethodCopy: string;
      };
    }
  | {
      type: 'SET_SHOW_CART';
      payload: {
        showCart: boolean;
      };
    }
  | {
      type: 'SET_CART_ID';
      payload: {
        revolveCartId: string;
      };
    }
  | {
      type: 'SET_ORDER';
      payload: {
        showCartConfirmation: boolean;
        revolveOrder?: Order;
        vivrelleOrder?: Order;
        errorSubmitOrder?: string;
      };
    }
  | {
      type: 'SET_BILL';
      payload: {
        bill: Bill;
      };
    }
  | {
      type: 'START_NEW_CHAT';
    }
  | {
      type: 'WISHLIST_ADD_ITEM';
      payload: {
        wishlistUpdated: boolean;
      };
    }
  | {
      type: 'SET_ADDRESSES';
      payload: {
        addresses: EPCCAddress[];
      };
    }
  | {
      type: 'LIMIT_REACHED';
      payload: {
        limitReached: boolean;
      };
    }
  | {
      type: 'SET_PROCESSING';
      payload: {
        isProcessing: boolean;
      };
    };

const reducer = (state: EllaState, action: EllaAction): EllaState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const itemExists = state.cart.some(
        (item) => item.sku === action.payload.cartItem.sku
      );

      if (itemExists) {
        return state;
      }
      return {
        ...state,
        cart: [...state.cart, action.payload.cartItem],
        isProcessing: false
      };
    }

    case 'REMOVE_FROM_CART': {
      return {
        ...state,
        cart: state.cart.filter(
          (item) => item.id !== action.payload.cartItemId
        ),
        isProcessing: false
      };
    }

    case 'SET_SHIPPING_METHOD': {
      return {
        ...state,
        shippingMethodId: action.payload.shippingMethodId,
        shippingMethodCopy: action.payload.shippingMethodCopy
      };
    }

    case 'SET_SHOW_CART': {
      return {
        ...state,
        showCart: action.payload.showCart,
        showCartConfirmation: false
      };
    }

    case 'SET_CART_ID': {
      return {
        ...state,
        revolveCartId: action.payload.revolveCartId
      };
    }

    case 'SET_ORDER': {
      return {
        ...state,
        showCart: false,
        showCartConfirmation: action.payload.showCartConfirmation,
        revolveOrder: action.payload.revolveOrder,
        vivrelleOrder: action.payload.vivrelleOrder,
        errorSubmitOrder: action.payload.errorSubmitOrder || undefined
      };
    }

    case 'SET_BILL': {
      // Bill is being updated with new values
      return {
        ...state,
        bill: action.payload.bill
      };
    }
    case 'WISHLIST_ADD_ITEM': {
      return {
        ...state,
        wishlistUpdated: action.payload.wishlistUpdated
      };
    }

    case 'START_NEW_CHAT': {
      return {
        ...initialState
      };
    }

    case 'SET_ADDRESSES': {
      return {
        ...state,
        addresses: action.payload.addresses
      };
    }

    case 'LIMIT_REACHED': {
      return {
        ...state,
        limitReached: action.payload.limitReached
      };
    }

    case 'SET_PROCESSING': {
      return {
        ...state,
        isProcessing: action.payload.isProcessing
      };
    }

    default: {
      return state;
    }
  }
};

const initialState: EllaState = {
  bill: {
    subtotal: 0,
    shipping: 0,
    taxes: 0,
    discountPercentage: 0,
    taxRate: 0
  },
  cart: [] as ProductCardData[],
  revolveCartId: undefined,
  shippingMethodId: '',
  shippingMethodCopy: '',
  showCart: false,
  showCartConfirmation: false,
  wishlistUpdated: false,
  limitReached: false,
  addresses: [],
  vivrelleOrder: undefined,
  revolveOrder: undefined,
  errorSubmitOrder: undefined,
  isProcessing: false
};

function Ella(props: EllaProps) {
  const {
    className = '',
    addresses: initialAddresses = [],
    shippingMethods = [],
    startNewStoreChat = false
  } = props;

  useEffect(() => {
    if (startNewStoreChat) {
      handleNewChat('TooMuchTimeElapsed');
    }
  }, [startNewStoreChat]);

  const fetchUserAddresses = useCallback(async () => {
    try {
      const response = await fetch(`/api/user/addresses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const addresses = data.data.addresses || [];
        return addresses;
      }
      return [];
    } catch (error) {
      console.error('Error fetching user addresses:', error);
      return [];
    }
  }, []);

  const handleLoginSuccess = useCallback(async () => {
    const addresses = await fetchUserAddresses();
    dispatch({
      type: 'SET_ADDRESSES',
      payload: {
        addresses
      }
    });
  }, [fetchUserAddresses]);

  const getVivrelleCart = useCallback(async () => {
    const cart = await fetchVivrelleCart();
    cart.items.map((item: any) => {
      dispatch({
        type: 'ADD_TO_CART',
        payload: {
          cartItem: {
            name: item.productCardData.name,
            store: ELLA_RETAILER.VIVRELLE,
            price: item.productCardData.price || 0,
            category: item.productCardData.category,
            sku: item.productCardData.sku,
            id: item.id,
            link: `/products/${item.slug}`,
            mainImage: item.productCardData.mainImage,
            item_group_id: item.id,
            tier: item.productCardData.tier,
            borrowed: false,
            brand: item.productCardData.brand,
            slug: item.productCardData.slug
          }
        }
      });
    });
  }, []);
  const { value: user } = useUser();
  const { closet } = useContextBroker();
  const { value: cartValue } = useCart();
  const { metadata } = useEllaAnalytics();
  const [chatId, setChatId] = useState<string>(uuidv4());

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    addresses: initialAddresses
  });

  useOnMount(() => {
    analytics.track('Ella AI - Page Viewed', {
      ...metadata
    });
  });

  // Store dispatch in window for cart creation only on client side
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.ellaDispatch = dispatch;
      if (shippingMethods.length > 0) {
        dispatch({
          type: 'SET_SHIPPING_METHOD',
          payload: {
            shippingMethodId: shippingMethods[0].id,
            shippingMethodCopy: shippingMethods[0].label
          }
        });
      }
    }
  }, [dispatch, shippingMethods]);

  useEffect(() => {
    if (user) {
      getVivrelleCart();
    }
  }, [user, getVivrelleCart]);

  const [itemsInCartOrClosetPrompts, setItemsInCartOrClosetPrompts] = useState<
    EllaPromptObject[]
  >([]);

  // Wishlist updated temporary show a toast
  useEffect(() => {
    if (state.wishlistUpdated) {
      const timer = setTimeout(() => {
        dispatch({
          type: 'WISHLIST_ADD_ITEM',
          payload: { wishlistUpdated: false }
        });
      }, 5000); // Hide after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [state.wishlistUpdated]);

  // Limit reached temporary show a toast
  useEffect(() => {
    if (state.limitReached) {
      const timer = setTimeout(() => {
        dispatch({
          type: 'LIMIT_REACHED',
          payload: { limitReached: false }
        });
      }, 5000); // Hide after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [state.limitReached]);

  const accountIsPastDue = closet.closet?.error?.status === 402;

  const isUserInLimitedState =
    user === undefined ||
    user.status === USER_STATUS.PENDING ||
    user.status === USER_STATUS.PENDING_MORE_INFO ||
    user.status === USER_STATUS.WAITLIST ||
    user.status === USER_STATUS.FROZEN ||
    user.status === USER_STATUS.DENIED ||
    user.status === USER_STATUS.CANCELLED ||
    user.isLocked ||
    user.isPastDue ||
    accountIsPastDue;

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    setMessages,
    error,
    reload,
    status: chatStatus
  } = useChat({
    api: '/api/ella',
    keepLastMessageOnError: true,
    id: chatId?.toString(),
    body: {
      customerCloset: closet?.closet,
      isUserInLimitedState
    }
  });

  const isLoading = chatStatus === 'streaming' || chatStatus === 'submitted';

  // Calculate tax rate when user addresses change
  useEffect(() => {
    const calculateTaxRate = async () => {
      const shippingAddress = state.addresses.find(
        (address: EPCCAddress) => address.isBilling === false
      );

      if (shippingAddress) {
        try {
          const taxRate = (await getTaxRateFromStripe(shippingAddress)) ?? 0;
          dispatch({
            type: 'SET_BILL',
            payload: {
              bill: {
                ...state.bill,
                taxRate: taxRate
              }
            }
          });
        } catch (error) {
          console.error('Error calculating tax rate:', error);
        }
      }
    };

    calculateTaxRate();
  }, [state.addresses, state.showCart]);

  const handleNewChat = useCallback(
    (reason: 'Clicked' | 'LoadError' | 'TooMuchTimeElapsed') => {
      analytics.track(`Ella AI Start New Chat - ${reason}`, {
        ...metadata
      });

      // Set new chat id, which will cause the useChat hook to start a new chat
      setChatId(uuidv4());

      // Clear session storage
      sessionStorage.removeItem('allRecommendations');
      sessionStorage.removeItem('nonVivrelleRecommendations');
      sessionStorage.removeItem('vivrelleRecommendations');
    },
    [metadata]
  );

  useEffect(() => {
    if (error) {
      let errorMessage = error?.message || 'Error getting response from chat';

      try {
        errorMessage = JSON.parse(error.message)?.error;
      } catch (e) {
        console.error('Error parsing error', e);
      }

      logEllaError(error as Error, {
        function: 'genericEllaError'
      });

      // For "Load Failed" error, just start new chat
      if (errorMessage.toLowerCase() === 'load failed') {
        handleNewChat('LoadError');
      } else {
        // TODO we should probably have a more graceful way to fail than this
        toast.error(errorMessage, {
          position: 'bottom-center',
          duration: 6000
        });
      }
    }
  }, [error, handleNewChat]);

  const prompts: EllaPromptObject[] = useMemo(() => {
    if (messages.length === 0) {
      return initialPrompts;
    }

    if (itemsInCartOrClosetPrompts.length > 0) {
      return itemsInCartOrClosetPrompts;
    }

    const suggestedReplies =
      messages[messages.length - 1]?.toolInvocations?.find(
        (invocation: { toolName: string }) =>
          invocation.toolName === 'suggestedReplies'
      )?.args?.suggestedReplies || [];

    return suggestedReplies.map((reply: any) => {
      return { prompt: reply.reply };
    });
  }, [messages, itemsInCartOrClosetPrompts]);

  const { isAtBottom, scrollToBottom } = useScrollToBottom();

  const handleItemsInCartOrClosetPrompts = (): {
    promptsToSet: EllaPromptObject[];
    assistantMessageToSet: Message;
  } | null => {
    setItemsInCartOrClosetPrompts([]);
    if (isUserInLimitedState) {
      return null;
    }
    const cartItems = cartValue.items;
    const closetItems: ClosetItem[] = (closet?.closet?.slots ?? []).reduce(
      (items: ClosetItem[], slot: ClosetSlot) => {
        if (slot.currentItem) {
          items.push(slot.currentItem);
        } else if (
          slot.returnedItem &&
          slot.returnedItem.status === ORDER_ITEM_STATUS_INTERNAL.RETURNED
        ) {
          items.push(slot.returnedItem);
        }
        return items;
      },
      []
    );

    const hasItemsInCartOrCloset =
      cartItems.length > 0 || closetItems.length > 0;

    if (!hasItemsInCartOrCloset || messages.length > 0) {
      return null;
    }
    let message: Message = {
      role: 'assistant',
      id: uuidv4().toString(),
      content: ''
    };

    // We need check with item is a highest tier
    // Premier < Classique < Couture
    // If you have any doubt of tier please check /how-it-works
    if (cartItems.length > 0) {
      let cartItemHighestTier = cartItems[0];
      if (cartItems.length > 1) {
        const tierValues: Record<string, number> = {
          [CLOSET_SLOT_TIER_STRING.PREMIER]: 1,
          [CLOSET_SLOT_TIER_STRING.CLASSIQUE]: 2,
          [CLOSET_SLOT_TIER_STRING.COUTURE]: 3
        };
        cartItemHighestTier = cartItems.reduce(
          (highest, current) =>
            tierValues[current?.product?.tier] >
            tierValues[highest?.product?.tier]
              ? current
              : highest,
          cartItems[0]
        );
      }

      message.content = `Would you like to build an outfit with the ${cartItemHighestTier.product.brand} ${cartItemHighestTier.product.category} you have in your cart?`;
      const promptOptionBasedOnCart: EllaPromptObject = {
        prompt: `Yes! Style a look around the ${cartItemHighestTier.product.brand} ${cartItemHighestTier.product.category}`,
        assistant: `First you must use the suggested replies tool to ask follow up questions about the occasion/user preferences. Then
        Style a REVOLVE outfit to wear with the following Vivrelle product: ${JSON.stringify(
          cartItemHighestTier
        )}. When it is time to recommend an outfit use the storeFilter "newRevolve" since we already have the Vivrelle item in the closet. Enter the included vivrelle item into session storage.`
      };
      return {
        promptsToSet: [
          promptOptionBasedOnCart,
          {
            prompt: PROMPT_SHOW_OTHER_VIVRELLE_ITEMS_COPY,
            assistant: `First you must use the suggested replies tool to ask follow up questions about the occasion/user preferences.
          Then recommend 1 Vivrelle item to borrow + 4 other Revolve/FWRD items to buy.
          Do not recommend the following item(s) already in their cart: ${JSON.stringify(
            cartItems.map((item) => item.productCardData)
          )}. In fact, do not mention the items in the user's cart or their cart at all.`
          }
        ],
        assistantMessageToSet: {
          role: 'assistant',
          id: uuidv4().toString(),
          content: message.content
        }
      };
    }
    message.content =
      closetItems.length > 1
        ? `Would you like to build an outfit with an item you’re already borrowing?`
        : `Would you like to build an outfit with the ${closetItems[0].productCardData.brand} ${closetItems[0].productCardData.category} you’re currently borrowing?`;
    const promptOptionsBasedOnCloset: EllaPromptObject[] = closetItems.map(
      (item) => {
        return {
          prompt: `Yes! Style a look around the ${item.productCardData.brand} ${item.productCardData.category}.`,
          assistant: `First you must use the suggested replies tool to ask follow up questions about the occasion/user preferences.
          Then Style a REVOLVE outfit to wear with the following Vivrelle product: ${JSON.stringify(
            item.productCardData
          )}. When it is time to recommend an outfit use the storeFilter "newRevolve" since we already have the Vivrelle item in the closet. Enter the included vivrelle item into session storage.`
        };
      }
    );
    return {
      promptsToSet: [
        ...promptOptionsBasedOnCloset,
        {
          prompt: PROMPT_SHOW_OTHER_VIVRELLE_ITEMS_COPY,
          assistant: `First you must use the suggested replies tool to ask follow up questions about the occasion/user preferences.
          Then recommend 1 Vivrelle item to borrow + 4 other Revolve/FWRD items to buy.
          Do not recommend the following item(s) already in their closet: ${JSON.stringify(
            closetItems.map((item) => item.productCardData)
          )}. In fact, do not mention the items in the user's closet or their closet at all.`
        }
      ],
      assistantMessageToSet: {
        role: 'assistant',
        id: uuidv4().toString(),
        content: message.content
      }
    };
  };

  // Custom handleSubmit wrapper to handle first message logic for user input specifically
  const firstMessageHandler = (e: any) => {
    // If this is the user's first message (no messages yet), check for cart/closet prompts
    if (messages.length === 0) {
      const promptsAndMsgToSet = handleItemsInCartOrClosetPrompts();
      if (promptsAndMsgToSet) {
        setItemsInCartOrClosetPrompts(promptsAndMsgToSet.promptsToSet);

        // Add both the user's message and the assistant's cart/closet prompt message
        const userMessage: Message = {
          role: 'user',
          id: uuidv4().toString(),
          content: input
        };

        setMessages([userMessage, promptsAndMsgToSet.assistantMessageToSet]);

        // Prevent default form submission to avoid page refresh
        e.preventDefault();
        // Return early to prevent the original user input from being submitted to AI
        // This pauses the flow to show the "Would you like to style around X item?" message
        return;
      }
    }

    // Call the original handleSubmit from useChat in all other cases
    handleSubmit(e);
  };

  const handlePromptClick = (e: any, prompt: EllaPromptObject) => {
    const promptsAndMsgToSet = handleItemsInCartOrClosetPrompts();

    analytics.track('Ella AI Prompt - Clicked', {
      ...metadata,
      itemInCart: cartValue.items.length > 0 || state.cart.length > 0,
      prompt: prompt.prompt
    });
    // Check if this is a prompt to style a look around a cart item
    if (
      prompt.assistant?.includes(
        'Enter the included vivrelle item into session storage.'
      )
    ) {
      // Extract the Vivrelle product from the assistant message
      try {
        // The product is included in the message as a JSON string
        const assistantText = prompt.assistant || '';
        const productMatch = assistantText.match(
          /with the following Vivrelle product: (\{[\s\S]*?\})/
        );

        if (productMatch && productMatch[1]) {
          const jsonString = productMatch[1];

          // Extract essential properties directly using regex
          const idMatch = jsonString.match(/"id"\s*:\s*"([^"]+)"/);
          const nameMatch = jsonString.match(/"name"\s*:\s*"([^"]+)"/);
          const brandMatch = jsonString.match(/"brand"\s*:\s*"([^"]+)"/);
          const imageMatch = jsonString.match(/"mainImage"\s*:\s*"([^"]+)"/);
          const categoryMatch = jsonString.match(/"category"\s*:\s*"([^"]+)"/);
          const tierMatch = jsonString.match(/"tier"\s*:\s*"([^"]+)"/);
          const imageLinkMatch = jsonString.match(
            /"image_link"\s*:\s*"([^"]+)"/
          );

          // Create a valid object with extracted properties
          const productData = {
            id: idMatch ? idMatch[1] : 'unknown-id',
            name: nameMatch ? nameMatch[1] : 'Unknown Product',
            brand: brandMatch ? brandMatch[1] : 'Unknown Brand',
            mainImage: imageMatch ? imageMatch[1] : '',
            category: categoryMatch ? categoryMatch[1] : '',
            tier: tierMatch ? tierMatch[1] : '',
            image_link: imageLinkMatch ? imageLinkMatch[1] : '',
            product: {
              id: idMatch ? idMatch[1] : 'unknown-id',
              name: nameMatch ? nameMatch[1] : 'Unknown Product',
              brand: brandMatch ? brandMatch[1] : 'Unknown Brand',
              mainImage: imageMatch ? imageMatch[1] : '',
              category: categoryMatch ? categoryMatch[1] : '',
              tier: tierMatch ? tierMatch[1] : ''
            }
          };

          // Add the item to sessionStorage so it's included in recommendations display
          if (typeof window !== 'undefined') {
            try {
              // Get existing Vivrelle items from sessionStorage
              const existingVivrelleItems = JSON.parse(
                sessionStorage.getItem('vivrelleRecommendations') || '[]'
              );

              // Create a product object in the format expected by sessionStorage
              const itemForStorage = {
                id: productData.id,
                name: productData.name,
                brand: productData.brand,
                description: '', // Use empty string since description might not exist
                mainImage: productData.mainImage,
                store: 'vivrelle',
                item_group_id: productData.id,
                updated_title: productData.name, // Use name since title might not exist
                image_link: productData.image_link, // Use mainImage directly
                category: productData.category || '',
                tier: productData.tier || ''
              };

              // Check if the item is already in the array to avoid duplicates
              const itemExists = existingVivrelleItems.some(
                (item: any) => item.id === itemForStorage.id
              );

              if (!itemExists) {
                // Add the new item to the beginning of the array
                existingVivrelleItems.unshift(itemForStorage);

                // Save back to sessionStorage
                sessionStorage.setItem(
                  'vivrelleRecommendations',
                  JSON.stringify(existingVivrelleItems)
                );
              }
            } catch (error) {
              console.error('Error adding item to sessionStorage:', error);
            }
          }
        } else {
          console.error(
            'Could not extract product data from assistant message'
          );
        }
      } catch (error) {
        console.error('Error parsing product from assistant message:', error);
      }
    }

    if (promptsAndMsgToSet) {
      setItemsInCartOrClosetPrompts(promptsAndMsgToSet.promptsToSet);
      setMessages([
        {
          role: 'user',
          content: prompt.prompt,
          id: uuidv4().toString()
        },
        promptsAndMsgToSet.assistantMessageToSet
      ]);
    } else {
      const msgsToSet = [
        ...messages,
        {
          role: 'user',
          content: prompt.prompt,
          id: uuidv4().toString()
        } as Message
      ];

      if (prompt.assistant) {
        msgsToSet.push({
          role: 'system',
          content: prompt.assistant,
          id: uuidv4().toString()
        });
      }

      setMessages(msgsToSet);

      reload();
    }
  };

  const orderConfirmationBlurb = () => {
    // Check if cart has both Vivrelle and Revolve items
    const hasVivrelleItems = state.vivrelleOrder || false;
    const hasRevolveItems = state.revolveOrder || false;
    const hasBothBrands = hasVivrelleItems && hasRevolveItems;

    let message =
      'Thank you for shopping with Vivrelle x REVOLVE! Your order includes items from';

    // Add which brands items included
    if (hasBothBrands) {
      message += ' both Vivrelle and REVOLVE,';
    } else if (!hasBothBrands && hasVivrelleItems) {
      message += ' Vivrelle,';
    } else {
      message += ' REVOLVE and/or FWRD,';
    }

    // Add shipping details
    if (hasBothBrands) {
      const isNYCPickup =
        state.vivrelleOrder && state.vivrelleOrder.isNYCPickup;
      if (isNYCPickup) {
        message +=
          ' which will be picked up from a Vivrelle store and directly from REVOLVE/FWRD.';
      } else {
        message += ' which will be shipped from each brand.';
      }
    } else if (hasVivrelleItems) {
      message += ' which will be shipped directly from Vivrelle.';
    } else {
      message += ' which will be shipped directly from their warehouse.';
    }

    message +=
      " Below, you'll find details on how each part of your order will be processed, so you know what to expect.";
    return message;
  };

  const [timeLeftPage, setTimeLeftPage] = useState<number>(Date.now());
  useEffect(() => {
    function handleVisibilityChange() {
      // When user leaves page, set timeLeftPage to current time
      if (document.visibilityState === 'hidden') {
        setTimeLeftPage(Date.now());
      } else {
        // If more than 5 minutes have passed since user left page, start new chat
        const threshold = 5 * 1000 * 60;
        if (Date.now() - timeLeftPage > threshold) {
          handleNewChat('TooMuchTimeElapsed');
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [timeLeftPage, handleNewChat]);

  const temporaryToast = (action: 'whishlisted' | 'limit_reached') => {
    return (
      <div className='Ella-temporary-toast'>
        <Text
          variant='title-xxs-500'
          uppercase
        >
          {action === 'whishlisted'
            ? 'Item Wishlisted'
            : 'Limit Reached, only 1 item'}
        </Text>
      </div>
    );
  };

  return (
    <div className={cn('Ella', className)}>
      {state.limitReached && temporaryToast('limit_reached')}

      {!state.showCart && !state.showCartConfirmation && (
        <div className='Ella-chat'>
          <EllaMessages
            closet={closet}
            dispatch={dispatch}
            isLoading={isLoading}
            handlePromptClick={handlePromptClick}
            handleNewChat={() => handleNewChat('Clicked')}
            messages={messages}
            accountIsPastDue={accountIsPastDue}
            state={state}
            isUserInLimitedState={isUserInLimitedState}
            onLoginSuccess={handleLoginSuccess}
          />

          <div className='Ella-scrollToBottom-container'>
            <ButtonScrollToBottom
              isAtBottom={isAtBottom}
              onClick={scrollToBottom}
            />
          </div>

          {prompts && prompts.length > 0 && (
            <div className='EllaPrompts'>
              {messages && messages.length === 0 ? (
                <div className='EllaPrompts-initial-container'>
                  {prompts.map((prompt, i) => {
                    return (
                      <EllaPrompt
                        key={i}
                        largePrompt={true}
                        prompt={prompt}
                        onClick={(e: any) => {
                          handlePromptClick(e, prompt);
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <SideScrollElement>
                  {prompts.map((prompt, i) => {
                    return (
                      <EllaPrompt
                        key={i}
                        prompt={prompt}
                        onClick={(e: any) => {
                          handlePromptClick(e, prompt);
                        }}
                      />
                    );
                  })}
                </SideScrollElement>
              )}
            </div>
          )}

          <EllaChatInput
            input={input}
            handleInputChange={handleInputChange}
            chatSubmit={firstMessageHandler}
            isFirstMessage={messages.length === 0}
            isShowingPredefinedPrompts={prompts.length > 0}
            disabled={isLoading}
          />
        </div>
      )}

      {state.showCart && (
        <EllaCart
          dispatch={dispatch}
          addresses={state.addresses}
          state={state}
          shippingMethods={shippingMethods}
        />
      )}

      {state.showCartConfirmation && (
        <div className='Ella-chat'>
          <EllaCartConfirmation
            state={state}
            shippingVivrelle={'2'}
            dispatch={dispatch}
            message={orderConfirmationBlurb()}
          />
        </div>
      )}
    </div>
  );
}

export default Ella;
